/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import { useState, useEffect, useMemo, type ChangeEvent } from 'react';

import {
  Dimmer,
  DimmerDimmable,
  Header,
  Icon,
  Input,
  Loader,
  Segment,
  Table,
} from '@mairistem/composants';

import { Section } from '@mairistem/administres-utils';

import type { useMentionAnts } from '@/hooks';
import type { ActeIndividu, MentionAnts } from '@/types';

interface Props {
  selectedIndividu: ActeIndividu | null;
  mentionAnts: ReturnType<typeof useMentionAnts>;
  selectedMention: MentionAnts | null;
  onMentionSelected: (mention: MentionAnts) => void;
}

const MentionBlock = ({
  selectedIndividu,
  mentionAnts,
  selectedMention,
  onMentionSelected,
}: Props) => {
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!selectedIndividu) {
      setSearch('');
    }
  }, [selectedIndividu]);

  const filteredMentions = useMemo(() => {
    const all = _.flatMap(mentionAnts.categories, (cat) => cat.mentions).sort(
      (a, b) =>
        (a.codificationAnts ?? '').localeCompare(b.codificationAnts ?? ''),
    );
    const term = search.toLowerCase().trim();
    if (_.isEmpty(term)) return all;
    return all.filter(
      (m) =>
        String(m.intitule ?? '')
          .toLowerCase()
          .includes(term) ||
        String(m.codeCategAnts ?? '')
          .toLowerCase()
          .includes(term) ||
        String(m.codificationAnts ?? '')
          .toLowerCase()
          .includes(term),
    );
  }, [mentionAnts.categories, search]);

  if (
    !selectedIndividu &&
    !mentionAnts.loading &&
    _.isEmpty(mentionAnts.categories)
  ) {
    return (
      <Section
        title="MENTIONS"
        style={{ height: '100%', minHeight: 0, overflow: 'hidden' }}
      >
        <Segment placeholder>
          <Header icon>
            <Icon name="hand point up outline" />
            Sélectionnez une personne pour voir les mentions
          </Header>
        </Segment>
      </Section>
    );
  }

  return (
    <Section
      title="MENTIONS"
      style={{ height: '100%', minHeight: 0, overflow: 'hidden' }}
    >
      <div style={{ flexShrink: 0, marginBottom: '0.5em' }}>
        <Input
          fluid
          icon="search"
          iconPosition="left"
          placeholder="Rechercher par intitulé, catégorie ou codification..."
          value={search}
          onChange={(
            _e: ChangeEvent<HTMLInputElement>,
            { value }: { value: string },
          ) => setSearch(value)}
        />
      </div>
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Table celled selectable style={{ tableLayout: 'fixed' }}>
          <Table.Header style={{ position: 'sticky', top: 0, zIndex: 2 }}>
            <Table.Row>
              <Table.HeaderCell content="Code" style={{ width: '20%' }} />
              <Table.HeaderCell
                content="Codification"
                style={{ width: '25%' }}
              />
              <Table.HeaderCell content="Intitulé" style={{ width: '55%' }} />
            </Table.Row>
          </Table.Header>
          <DimmerDimmable
            as={Table.Body}
            dimmed={mentionAnts.loading}
            {...(mentionAnts.loading && { style: { height: 80 } })}
          >
            <Dimmer active={mentionAnts.loading} inverted>
              <Loader indeterminate />
            </Dimmer>
            {_.isEmpty(filteredMentions) && !mentionAnts.loading ? (
              <Table.Row>
                <Table.Cell colSpan={3} textAlign="center" disabled>
                  Aucune mention trouvée
                </Table.Cell>
              </Table.Row>
            ) : (
              _.map(filteredMentions, (mention) => (
                <Table.Row
                  key={mention.identifiant}
                  active={selectedMention?.identifiant === mention.identifiant}
                  onClick={() => onMentionSelected(mention)}
                  style={{ cursor: 'pointer' }}
                >
                  <Table.Cell content={mention.codeCategAnts} />
                  <Table.Cell content={mention.codificationAnts} />
                  <Table.Cell content={mention.intitule} />
                </Table.Row>
              ))
            )}
          </DimmerDimmable>
        </Table>
      </div>
    </Section>
  );
};

export default MentionBlock;
